/* eslint-disable func-style */
import React from 'react';
import {useTranslation} from 'react-i18next';

import {showNewsModal} from 'src/containers/News';
import {
  useGetTenantDataQuery,
  useGetWhatsAppTariffChangeBannerDatesQuery,
} from 'src/containers/User/api';
import {TariffImage} from 'src/images';
import {isDateBetween} from 'src/utils';

import {useBannerConfig} from '../BannersConfig';
import {EResellerIds, useReseller} from '../User';
import {WhatsAppLicensePriceUpdatePlannedContent} from './SystemNews';
import {usePollNotificationsQuery} from './api';
import {
  ENotificationLevel,
  ENotificationStatus,
  ESystemNewsType,
  ESystemNotificationEntityType,
  ESystemNotificationType,
  EWebNotificationType,
  TSystemNews,
  TSystemNotification,
  TWebNotification,
  TWhatsAppLicensePriceNews,
} from './model';
import {businessArticlesNews, businessCasesNews} from './staticSystemNews';
import {sortNews} from './utils';

enum EFilterStrategy {
  FIRST_OF_TYPE = 'FIRST_OF_TYPE',
  ALL = 'ALL',
}

type TConfig<T> = {
  pollingInterval?: number;
  skip?: boolean;
  type: T | T[];
  filterStrategy?: EFilterStrategy;
};

type TWebConfig = TConfig<keyof typeof EWebNotificationType>;

function useWebNotifications<T extends TWebNotification>(config: TWebConfig): T[];

function useWebNotifications(config?: Omit<TWebConfig, 'type'>): TWebNotification[];

function useWebNotifications<T extends TWebNotification>(
  config: Partial<TWebConfig> = {},
): TWebNotification[] | T[] {
  const {data} = usePollNotificationsQuery(undefined, {
    pollingInterval: config.pollingInterval,
    skip: config.skip,
    skipPollingIfUnfocused: true,
  });

  if (!data) {
    return [];
  }

  if (config.type) {
    const types = Array.isArray(config.type) ? config.type : [config.type];

    return data.webNotifications.filter((notification): notification is T =>
      types.includes(notification.type),
    );
  }

  return data.webNotifications;
}

type TSystemConfig = TConfig<keyof typeof ESystemNotificationType>;

function useSystemNotifications<T extends TSystemNotification>(config: TSystemConfig): T[];

function useSystemNotifications(config?: Omit<TSystemConfig, 'type'>): TSystemNotification[];

function useSystemNotifications<T extends TSystemNotification>(
  config: Partial<TSystemConfig> = {},
): TSystemNotification[] | T[] {
  const {data} = usePollNotificationsQuery(undefined, {
    pollingInterval: config.pollingInterval,
    skip: config.skip,
    skipPollingIfUnfocused: true,
  });

  if (!data) {
    return [];
  }

  const systemNotifications =
    data.systemNotifications?.filter(
      (notification): notification is TSystemNotification =>
        notification.entityType === ESystemNotificationEntityType.NOTIFICATION,
    ) ?? [];

  if (config.type) {
    const types = Array.isArray(config.type) ? config.type : [config.type];

    return systemNotifications.filter((notification): notification is T =>
      types.includes(notification.type),
    );
  }

  return systemNotifications;
}

type TSystemNewsConfig = TConfig<keyof typeof ESystemNewsType>;

const EDNA_WA_TARIFF_CHANGE_NEWS_01_04_2025 = 'EDNA_WA_TARIFF_CHANGE_NEWS_01_04_2025';
const EDNA_WA_TARIFF_CHANGE_MODAL_01_04_2025 = 'EDNA_WA_TARIFF_CHANGE_MODAL_01_04_2025';

function useSystemNews<T extends TSystemNews>(config: TSystemNewsConfig): T[];

function useSystemNews(config?: Omit<TSystemNewsConfig, 'type'>): TSystemNews[];

function useSystemNews<T extends TSystemNews>(
  config: Partial<TSystemNewsConfig> = {},
): TSystemNews[] | T[] {
  const filterStrategy = config.filterStrategy ?? EFilterStrategy.ALL;
  const {t, i18n} = useTranslation();
  const {data: tenant} = useGetTenantDataQuery();
  const {data} = usePollNotificationsQuery(undefined, {
    pollingInterval: config.pollingInterval,
    skip: config.skip,
    skipPollingIfUnfocused: true,
  });
  const {data: bannerDates} = useGetWhatsAppTariffChangeBannerDatesQuery();
  const isOSKReseller = useReseller(EResellerIds.OSK_ID);

  const licensePriceUpdateNews = React.useMemo(() => {
    if (bannerDates && isDateBetween(bannerDates.startDate, bannerDates.endDate)) {
      return {
        type: ESystemNewsType.WhatsAppLicensePriceUpdatePlanned,
        entityType: ESystemNotificationEntityType.NEWS,
        id: EDNA_WA_TARIFF_CHANGE_NEWS_01_04_2025,
        status: ENotificationStatus.UNREAD,
        level: ENotificationLevel.WARNING,
        occurredAt: '',
        meta: {},
      } as TWhatsAppLicensePriceNews;
    }

    return undefined;
  }, [bannerDates]);

  const systemNotifications = React.useMemo(
    () =>
      [
        ...(data?.systemNotifications ?? []),
        licensePriceUpdateNews,
        ...(isOSKReseller && tenant
          ? businessArticlesNews(i18n.language).filter((article) =>
              article.meta.guard ? article.meta.guard(tenant) : true,
            )
          : []),
        ...(isOSKReseller ? businessCasesNews : []),
      ].filter(Boolean),
    [
      i18n.language,
      data,
      licensePriceUpdateNews,
      isOSKReseller,
      businessCasesNews,
      businessArticlesNews,
      tenant,
    ],
  );

  const {isBannerOpen: isModalOpen, closeBanner: closeModal} = useBannerConfig({
    id: EDNA_WA_TARIFF_CHANGE_MODAL_01_04_2025,
    category: 'NEWS',
  });

  React.useEffect(() => {
    if (!isModalOpen || !licensePriceUpdateNews) {
      return;
    }

    showNewsModal({
      onClose: closeModal,
      image: TariffImage,
      title: t(`Notifications:news.${ESystemNewsType.WhatsAppLicensePriceUpdatePlanned}.title`),
      children: <WhatsAppLicensePriceUpdatePlannedContent item={licensePriceUpdateNews} />,
    });
  }, [isModalOpen, licensePriceUpdateNews]);

  if (!systemNotifications) {
    return [];
  }

  const news = sortNews(
    systemNotifications?.filter(
      (notification): notification is TSystemNews =>
        // @ts-expect-error: ok
        notification.entityType === ESystemNotificationEntityType.NEWS,
    ) ?? [],
  );

  if (config.type) {
    const types = Array.isArray(config.type) ? config.type : [config.type];

    if (filterStrategy === EFilterStrategy.FIRST_OF_TYPE) {
      // @ts-expect-error: ok
      return types.map((type) => news.find((newsItem) => newsItem.type === type)).filter(Boolean);
    }

    return news.filter((newsItem) => types.includes(newsItem.type));
  }

  return news;
}

export {useSystemNews, useWebNotifications, useSystemNotifications, EFilterStrategy};
/* eslint-enable func-style */
